import { makeAutoObservable } from "mobx";

import {
  apiGetPhysicians,
  apiGetUsersMonitoringUsers,
  PhysicianWithPatientsResponse,
  UserReferenceResponse,
} from "../../api";
import { ApiConnector } from "../../api/api-connector";
import {
  hasMedicalAccess,
  hasMonitoringAccess,
} from "../../constants/permissions";

export type MonitoringKind = "medical" | "technical";

export interface MonitoringUserInfo {
  uuid: string;
  username: string;
  kind: MonitoringKind;
}

/**
 * Classifies monitoring-user uuids as medical/technical from the
 * monitoring-users endpoint's permission sets: users holding the medical
 * group bit count as "medical", the rest as "technical". Loaded for
 * monitoring-tier users only; for everyone else (and on fetch failure) every
 * user classifies as "technical" so pages render unchanged.
 *
 * Endpoints that already embed `assigned_monitoring_users` on their physician
 * objects (the waiting rooms) can turn those refs into `MonitoringUserInfo[]`
 * directly via `usersFor` — no extra physicians fetch needed. Endpoints whose
 * physician objects don't carry the field (the patients list) still need
 * `MonitoringAssignments` below to join it in from a separate call.
 */
export class MonitoringUsersDirectory {
  medicalUuids = new Set<string>();

  constructor(private api: ApiConnector) {
    makeAutoObservable(this);
  }

  async init() {
    if (!hasMonitoringAccess(this.api.permissions)) return;
    try {
      const { data } = await apiGetUsersMonitoringUsers();
      this.medicalUuids = new Set(
        data.users
          .filter((user) => hasMedicalAccess(user.access_policy.permissions))
          .map((user) => user.uuid),
      );
    } catch {
      // Enrichment only — pages render without kind classification when it fails.
    }
  }

  usersFor(
    refs: UserReferenceResponse[] | null | undefined,
  ): MonitoringUserInfo[] {
    return (refs ?? []).map((user) => ({
      uuid: user.uuid,
      username: user.username,
      kind: this.medicalUuids.has(user.uuid) ? "medical" : "technical",
    }));
  }
}

/**
 * Physician-uuid → assigned monitoring users, joined from the physicians
 * endpoint (whose response doesn't embed the info) and
 * `MonitoringUsersDirectory`. Loaded for monitoring-tier users only; for
 * everyone else (and on fetch failure) the map stays empty so pages render
 * unchanged.
 */
export class MonitoringAssignments {
  byPhysician = new Map<string, MonitoringUserInfo[]>();

  constructor(private api: ApiConnector) {
    makeAutoObservable(this);
  }

  forPhysician(uuid: string | undefined): MonitoringUserInfo[] {
    return (uuid && this.byPhysician.get(uuid)) || [];
  }

  findUser(uuid: string): MonitoringUserInfo | undefined {
    for (const users of this.byPhysician.values()) {
      const match = users.find((user) => user.uuid === uuid);
      if (match) return match;
    }
    return undefined;
  }

  async init() {
    if (!hasMonitoringAccess(this.api.permissions)) return;
    try {
      const directory = new MonitoringUsersDirectory(this.api);
      const [, physicians] = await Promise.all([
        directory.init(),
        apiGetPhysicians(),
      ]);
      this.setAssignments(
        buildAssignments(physicians.data.physicians, directory),
      );
    } catch {
      // Enrichment only — pages render without bubbles when it fails.
    }
  }

  private setAssignments(byPhysician: Map<string, MonitoringUserInfo[]>) {
    this.byPhysician = byPhysician;
  }
}

function buildAssignments(
  physicians: PhysicianWithPatientsResponse[],
  directory: MonitoringUsersDirectory,
): Map<string, MonitoringUserInfo[]> {
  const byPhysician = new Map<string, MonitoringUserInfo[]>();
  for (const physician of physicians) {
    const users = directory.usersFor(physician.assigned_monitoring_users);
    if (users.length > 0) byPhysician.set(physician.uuid, users);
  }
  return byPhysician;
}
